// Stripe Connect from inside the Sellwild app.
//
// Stripe does not support Account Links inside a WebView, and the app's
// WebView does not allow connect.stripe.com anyway, so `location.href = url`
// in the app handed the form to the phone's own browser. When the seller
// finished, Stripe sent them to the Sellwild WEBSITE, and they had to find
// their way back to the app and tap "Check again" by hand.
//
// In the app the link now opens in the in-app browser (SFSafariViewController
// on iOS, Custom Tabs on Android). The server mints the link with
// /api/wallet/stripe-return as its return and refresh URL, and that route
// 303s the browser to `sellwildapp://wallet/stripe-return`. The app hears
// that as `appUrlOpen`, closes the browser and re-reads the payout status.
// On the web nothing changes: the tab follows the link.

import { Capacitor } from '@capacitor/core';

export { STRIPE_RETURN_DEEP_LINK, stripeReturnDeepLink } from './stripe-return-link';

/** Deep links under this prefix mean "the seller is back from Stripe". */
const WALLET_DEEP_LINK_PREFIX = 'sellwildapp://wallet/';

/** True inside the iOS or Android app, false in any web browser. */
export const isNativeApp = (): boolean =>
  typeof window !== 'undefined' && Capacitor.isNativePlatform();

/**
 * The request-body flag that asks the server for an app-bound Connect link.
 * Empty on the web, so web requests are byte-for-byte what they were.
 */
export const nativeFlag = (): { native?: true } => (isNativeApp() ? { native: true } : {});

/**
 * Send the seller to a Stripe Connect link.
 *
 * `onReturn` runs once, in the app only, when the seller comes back: either
 * Stripe's return page opened the wallet deep link, or the seller closed the
 * in-app browser with Done. Both mean the status on screen may be stale.
 */
export async function followStripeConnectLink(url: string, onReturn?: () => void): Promise<void> {
  if (!isNativeApp()) {
    window.location.href = url;
    return;
  }

  const [{ Browser }, { App }] = await Promise.all([
    import('@capacitor/browser'),
    import('@capacitor/app'),
  ]);

  let settled = false;
  const listeners: Array<Promise<{ remove: () => Promise<void> }>> = [];
  const detach = () => {
    for (const handle of listeners) void handle.then((h) => h.remove());
  };
  const finish = (closeBrowser: boolean) => {
    if (settled) return;
    settled = true;
    detach();
    if (closeBrowser) void Browser.close();
    onReturn?.();
  };

  listeners.push(
    App.addListener('appUrlOpen', (event) => {
      if (event.url.startsWith(WALLET_DEEP_LINK_PREFIX)) finish(true);
    })
  );
  // Done in the browser toolbar: the seller may have finished or given up.
  // Either way the browser is already gone and the status is worth a re-read.
  listeners.push(Browser.addListener('browserFinished', () => finish(false)));

  try {
    await Browser.open({ url });
  } catch (error) {
    // No browser, so no return to wait for. Leave nothing listening for the
    // next tap to trip over, and let the caller report the failure.
    settled = true;
    detach();
    throw error;
  }
}
